import { t } from "i18next";
import { useState } from "react";
import { deviceLinks } from "../../../api/adminDirectory";
import { deleteDevice } from "../../../api/device";
import { isLegacyDeviceType } from "../../../api/legacyPacks";
import { enumsectionToI18nKey } from "../../../generated/enums";
import { Account, DeviceWithoutLegacyPassword } from "../../../generated/types";
import { useAdminDirectory } from "../../../hooks/useAdminDirectory";
import { useDeviceConnections } from "../../../hooks/useDeviceConnections";
import { isAdminAccount } from "../../../utils/roles";
import { DeleteModal } from "../../common/DeleteModal";
import { TableDetails, TicketNote } from "../../common/TableDetails";
import { TitleSection } from "../../common/TitleSection";
import { sectionToIcon } from "../../type/default";
import { BreedingSection } from "../breeding/BreedingSection";
import { BreedingTicket } from "../breeding/BreedingTicket";
import { CreateBreedingModal } from "../breeding/CreateBreedingModal";
import { BuildingSection } from "../building/BuildingSection";
import { BuildingTicket } from "../building/BuildingTicket";
import { AccountGroups } from "../user/AccountGroups";
import { CompanySection } from "../user/CompanySection";
import { UserTicket } from "../user/UserTicket";
import { DeviceSection } from "./DeviceSection";
import { useLegacyPacks } from "./LegacyPacks";
import { LicenseModal, useDeviceLicenses } from "./LicenseModal";

type DeviceTicketProps = {
  device: DeviceWithoutLegacyPassword;
  onClose: () => void;
  useBackIcon?: boolean;
  /** Rafraîchit la page sous les tickets. */
  onDataRefresh?: () => void;
};

export const DeviceTicket = ({
  device: initialDevice,
  onClose,
  useBackIcon = true,
  onDataRefresh,
}: DeviceTicketProps) => {
  const [selectedUser, setSelectedUser] = useState<Account | null>(null);
  const [showBuilding, setShowBuilding] = useState<boolean>(false);
  const [showBreeding, setShowBreeding] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateBreedingOpen, setIsCreateBreedingOpen] = useState(false);
  const [isLicenseOpen, setIsLicenseOpen] = useState(false);

  const { directory, isLoading, failed, reload } = useAdminDirectory();
  const { device, building, breeding, company, accounts, installers } =
    deviceLinks(directory, initialDevice);
  const refresh = () => {
    reload();
    onDataRefresh?.();
  };

  const canDeleteDevice = isAdminAccount();

  // Licences du produit : seuls les serenity en ont. Chargées ici pour que le résumé du
  // ticket et le modal d'activation partagent le même état (et le même rafraîchissement).
  const hasLicenses = device.type === "serenity";
  const { activeCodes, reload: reloadLicenses } = useDeviceLicenses(
    device.uuid,
    hasLicenses
  );
  // Produits legacy : leurs packs, en lecture seule (modifiés sur la machine).
  const isLegacy = isLegacyDeviceType(device.type);
  const legacyPacks = useLegacyPacks(device.uuid, isLegacy);
  const connection = useDeviceConnections()[device.uuid];

  const handleConfirmDelete = () => {
    setIsDeleteModalOpen(false);
    deleteDevice(device.uuid)
      .then(() => {
        onClose();
        refresh();
      })
      .catch((error) => console.error(error));
  };

  return (
    <div className="space-y-4">
      <TableDetails
        title={t(enumsectionToI18nKey["section_device"])}
        icon={sectionToIcon.section_device}
        onClose={onClose}
        onDelete={
          canDeleteDevice ? () => setIsDeleteModalOpen(true) : undefined
        }
        deleteText={t("delete-device")}
        useBackIcon={useBackIcon}
      >
        <div className="p-8 space-y-4">
          <DeviceSection
            device={device}
            detailLevel="maxi"
            licenses={activeCodes}
            legacyPacks={isLegacy ? legacyPacks : undefined}
            onLicenseOpen={() => setIsLicenseOpen(true)}
            connection={connection}
          />
          {isLoading || failed ? (
            <TicketNote>
              {failed
                ? String(t("ticket-links-error"))
                : `${String(t("loading"))}…`}
            </TicketNote>
          ) : (
            <>
              <TitleSection
                title={String(t(enumsectionToI18nKey["section_building"]))}
                icon={sectionToIcon.section_building}
              />
              {building ? (
                <div className="border-l-2 border-secondary pl-4 ml-2">
                  <BuildingSection
                    building={building}
                    onClick={() => setShowBuilding(true)}
                  />
                </div>
              ) : (
                <TicketNote>{String(t("ticket-no-building"))}</TicketNote>
              )}
              <TitleSection
                title={String(t(enumsectionToI18nKey["section_breeding"]))}
                icon={sectionToIcon.section_breeding}
                onClick={
                  !breeding ? () => setIsCreateBreedingOpen(true) : undefined
                }
              />
              {breeding ? (
                <div className="border-l-2 border-secondary pl-4 ml-2">
                  <BreedingSection
                    breeding={breeding}
                    onClick={() => setShowBreeding(true)}
                  />
                </div>
              ) : (
                <TicketNote>{String(t("ticket-no-breeding"))}</TicketNote>
              )}

              <TitleSection
                title={String(t(enumsectionToI18nKey["section_accounts"]))}
                icon={sectionToIcon.section_accounts}
              />
              {accounts.length + installers.length > 0 ? (
                <AccountGroups
                  accounts={[...accounts, ...installers]}
                  directory={directory}
                  onSelect={setSelectedUser}
                />
              ) : (
                <TicketNote>{String(t("ticket-no-account"))}</TicketNote>
              )}

              {company && <CompanySection company={company} />}
            </>
          )}
        </div>
      </TableDetails>

      {selectedUser && (
        <UserTicket
          account={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDataRefresh={onDataRefresh}
        />
      )}

      {showBuilding && building && (
        <BuildingTicket
          building={building}
          onClose={() => setShowBuilding(false)}
          onDataRefresh={onDataRefresh}
        />
      )}

      {showBreeding && breeding && (
        <BreedingTicket
          breeding={breeding}
          onClose={() => setShowBreeding(false)}
          onDataRefresh={onDataRefresh}
        />
      )}

      <CreateBreedingModal
        companyId={device.id_company}
        companyName={company?.company_name ?? ""}
        accountNames={accounts.map(
          (account) => `${account.last_name} ${account.first_name}`
        )}
        isOpen={isCreateBreedingOpen}
        onClose={() => setIsCreateBreedingOpen(false)}
        onSuccess={refresh}
      />

      {hasLicenses && (
        <LicenseModal
          isOpen={isLicenseOpen}
          onClose={() => setIsLicenseOpen(false)}
          device={device}
          companyId={device.id_company}
          activeCodes={activeCodes}
          onSaved={reloadLicenses}
        />
      )}

      <DeleteModal
        title={t("delete-device")}
        warningText={t("delete-device-warning", { name: device.name })}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        handleConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
};
